import InternalChatsAPI from '../../api/internalChats';

export const initialState = {
  chats: [],
  activeChat: null,
  // messages keyed by chatId: { [chatId]: Message[] }
  messages: {},
  agentsCache: [],
  // unread counts keyed by chatId: { [chatId]: number }
  unreadByChat: {},
  // ephemeral typing state: { [chatId]: { [userId]: { userName } } }
  typingByChat: {},
  uiFlags: {
    isFetchingChats: false,
    isFetchingMessages: false,
    isSendingMessage: false,
    isCreatingDm: false,
    isCreatingGroup: false,
    isCreatingChannel: false,
  },
};

const state = { ...initialState };

const getters = {
  getChats($state) {
    return $state.chats;
  },
  getChannels($state) {
    return $state.chats.filter(c => c.chat_type === 'channel');
  },
  getGroups($state) {
    return $state.chats.filter(c => c.chat_type === 'group');
  },
  getDms($state) {
    return $state.chats.filter(c => c.chat_type === 'direct_message');
  },
  getActiveChat($state) {
    return $state.activeChat;
  },
  getMessages($state) {
    return chatId => $state.messages[chatId] || [];
  },
  getAgentsCache($state) {
    return $state.agentsCache;
  },
  getUnreadByChat($state) {
    return $state.unreadByChat;
  },
  getTypingNames($state) {
    return chatId =>
      Object.values($state.typingByChat[chatId] || {}).map(u => u.userName);
  },
  totalUnread($state) {
    return Object.values($state.unreadByChat).reduce(
      (sum, count) => sum + (count || 0),
      0
    );
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

const actions = {
  async list({ commit }) {
    commit('SET_UI_FLAG', { isFetchingChats: true });
    try {
      const response = await InternalChatsAPI.list();
      commit('SET_CHATS', response.data);
    } catch (error) {
      // Ignore silently — UI stays empty
    } finally {
      commit('SET_UI_FLAG', { isFetchingChats: false });
    }
  },

  async get({ commit }, id) {
    try {
      const response = await InternalChatsAPI.show(id);
      commit('UPDATE_CHAT', response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  },

  async createDm({ commit }, userId) {
    commit('SET_UI_FLAG', { isCreatingDm: true });
    // BUG-1 (audit 2026-06-16): let the original axios error propagate so the
    // caller can surface the backend message (e.g. self-DM 422). The finally
    // still resets the UI flag.
    try {
      const response = await InternalChatsAPI.createDm(userId);
      commit('ADD_CHAT', response.data);
      return response.data;
    } finally {
      commit('SET_UI_FLAG', { isCreatingDm: false });
    }
  },

  async createGroup({ commit }, { name, description, memberIds, avatar }) {
    commit('SET_UI_FLAG', { isCreatingGroup: true });
    try {
      const response = await InternalChatsAPI.createGroup({
        name,
        description,
        memberIds,
        avatar,
      });
      commit('ADD_CHAT', response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit('SET_UI_FLAG', { isCreatingGroup: false });
    }
  },

  async createChannel({ commit }, { name, description }) {
    commit('SET_UI_FLAG', { isCreatingChannel: true });
    try {
      const response = await InternalChatsAPI.createChannel({
        name,
        description,
      });
      commit('ADD_CHAT', response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit('SET_UI_FLAG', { isCreatingChannel: false });
    }
  },

  async update({ commit }, { id, ...payload }) {
    // BUG-2 (audit 2026-06-16): propagate the original axios error so callers can
    // surface the backend message.
    const response = await InternalChatsAPI.update(id, payload);
    commit('UPDATE_CHAT', response.data);
    return response.data;
  },

  async destroy({ commit }, id) {
    // BUG-2/BUG-3 (audit 2026-06-16): propagate the original axios error.
    await InternalChatsAPI.destroy(id);
    commit('REMOVE_CHAT', id);
  },

  async addMembers({ commit }, { id, userIds }) {
    try {
      const response = await InternalChatsAPI.addMembers(id, userIds);
      commit('UPDATE_CHAT', response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  },

  async removeMember({ commit }, { id, userId }) {
    try {
      const response = await InternalChatsAPI.removeMember(id, userId);
      // If server returns the updated chat, update it; otherwise remove it from list
      if (response.data && response.data.id) {
        commit('UPDATE_CHAT', response.data);
      } else {
        // User left the group — remove from local list
        commit('REMOVE_CHAT', id);
      }
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  },

  async markRead({ commit }, id) {
    try {
      await InternalChatsAPI.markRead(id);
      commit('RESET_UNREAD', id);
    } catch (error) {
      // Ignore
    }
  },

  async fetchMessages({ commit }, { chatId, offset = 0, limit = 50 }) {
    // [polish] only set isFetchingMessages for initial load (offset=0),
    // not for pagination loads which are tracked by isLoadingOlder in the component
    const isInitialLoad = offset === 0;
    if (isInitialLoad) commit('SET_UI_FLAG', { isFetchingMessages: true });
    try {
      const response = await InternalChatsAPI.listMessages(chatId, {
        offset,
        limit,
      });
      const messages = response.data?.data || response.data || [];
      if (isInitialLoad) {
        commit('SET_MESSAGES', { chatId, messages });
      } else {
        // Prepend older messages without clobbering newer ones
        commit('PREPEND_MESSAGES', { chatId, messages });
      }
      return messages;
    } catch (error) {
      throw new Error(error);
    } finally {
      if (isInitialLoad) commit('SET_UI_FLAG', { isFetchingMessages: false });
    }
  },

  async sendMessage({ commit }, { chatId, content, attachments = [] }) {
    commit('SET_UI_FLAG', { isSendingMessage: true });
    try {
      const response = await InternalChatsAPI.createMessage(chatId, {
        content,
        attachments,
      });
      commit('ADD_MESSAGE', { chatId, message: response.data });
      return response.data;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit('SET_UI_FLAG', { isSendingMessage: false });
    }
  },

  async updateMessage({ commit }, { chatId, messageId, content }) {
    try {
      const response = await InternalChatsAPI.updateMessage(
        chatId,
        messageId,
        content
      );
      commit('UPDATE_MESSAGE', { chatId, message: response.data });
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  },

  async deleteMessage({ commit }, { chatId, messageId }) {
    try {
      await InternalChatsAPI.deleteMessage(chatId, messageId);
      commit('REMOVE_MESSAGE', { chatId, messageId });
    } catch (error) {
      throw new Error(error);
    }
  },

  async fetchAgents({ commit }, query = '') {
    try {
      const response = await InternalChatsAPI.agents(query);
      commit('SET_AGENTS', response.data);
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  },

  setActiveChat({ commit }, chat) {
    commit('SET_ACTIVE_CHAT', chat);
  },
};

const mutations = {
  SET_UI_FLAG($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },

  SET_CHATS($state, chats) {
    $state.chats = chats;
    // Seed unread counts from API payload
    chats.forEach(chat => {
      if (
        chat.unread_count !== undefined &&
        $state.unreadByChat[chat.id] === undefined
      ) {
        $state.unreadByChat = {
          ...$state.unreadByChat,
          [chat.id]: chat.unread_count || 0,
        };
      }
    });
  },

  ADD_CHAT($state, chat) {
    const exists = $state.chats.some(c => c.id === chat.id);
    if (!exists) {
      $state.chats = [...$state.chats, chat];
    }
  },

  UPDATE_CHAT($state, updatedChat) {
    const index = $state.chats.findIndex(c => c.id === updatedChat.id);
    if (index !== -1) {
      const chats = [...$state.chats];
      chats.splice(index, 1, updatedChat);
      $state.chats = chats;
    } else {
      $state.chats = [...$state.chats, updatedChat];
    }
    if ($state.activeChat && $state.activeChat.id === updatedChat.id) {
      $state.activeChat = updatedChat;
    }
  },

  REMOVE_CHAT($state, id) {
    $state.chats = $state.chats.filter(c => c.id !== id);
    if ($state.activeChat && $state.activeChat.id === id) {
      $state.activeChat = null;
    }
    const unread = { ...$state.unreadByChat };
    delete unread[id];
    $state.unreadByChat = unread;
  },

  SET_ACTIVE_CHAT($state, chat) {
    $state.activeChat = chat;
  },

  SET_MESSAGES($state, { chatId, messages }) {
    $state.messages = { ...$state.messages, [chatId]: messages };
  },

  // [polish] prepend older messages (pagination) without clobbering newer ones
  PREPEND_MESSAGES($state, { chatId, messages }) {
    const existing = $state.messages[chatId] || [];
    const existingIds = new Set(existing.map(m => m.id));
    const newOnes = messages.filter(m => !existingIds.has(m.id));
    $state.messages = {
      ...$state.messages,
      [chatId]: [...newOnes, ...existing],
    };
  },

  ADD_MESSAGE($state, { chatId, message }) {
    const existing = $state.messages[chatId] || [];
    const alreadyExists = existing.some(m => m.id === message.id);
    if (!alreadyExists) {
      $state.messages = {
        ...$state.messages,
        [chatId]: [...existing, message],
      };
    }
  },

  // Merge (not replace): real-time `message.updated` broadcasts carry only a
  // partial payload (id/content/edited/edited_at). Merging preserves fields the
  // bubble needs (sender, attachments, created_at). The REST update returns a
  // full message, so merging is a no-op-equivalent there.
  UPDATE_MESSAGE($state, { chatId, message }) {
    const existing = $state.messages[chatId] || [];
    const idx = existing.findIndex(m => m.id === message.id);
    if (idx === -1) return;
    const updated = [...existing];
    updated.splice(idx, 1, { ...existing[idx], ...message });
    $state.messages = { ...$state.messages, [chatId]: updated };
  },

  REMOVE_MESSAGE($state, { chatId, messageId }) {
    const existing = $state.messages[chatId] || [];
    $state.messages = {
      ...$state.messages,
      [chatId]: existing.filter(m => m.id !== messageId),
    };
  },

  INCREMENT_UNREAD($state, chatId) {
    $state.unreadByChat = {
      ...$state.unreadByChat,
      [chatId]: ($state.unreadByChat[chatId] || 0) + 1,
    };
  },

  RESET_UNREAD($state, chatId) {
    $state.unreadByChat = { ...$state.unreadByChat, [chatId]: 0 };
  },

  SET_AGENTS($state, agents) {
    $state.agentsCache = agents;
  },

  SET_TYPING($state, { chatId, userId, userName }) {
    const chatTyping = {
      ...($state.typingByChat[chatId] || {}),
      [userId]: { userName },
    };
    $state.typingByChat = { ...$state.typingByChat, [chatId]: chatTyping };
  },

  CLEAR_TYPING($state, { chatId, userId }) {
    const chatTyping = { ...($state.typingByChat[chatId] || {}) };
    delete chatTyping[userId];
    $state.typingByChat = { ...$state.typingByChat, [chatId]: chatTyping };
  },

  // Update a participant's last_read_at (read receipt) on the chat objects
  // held in the list and as the active chat, so "seen" indicators react.
  SET_PARTICIPANT_READ($state, { chatId, userId, lastReadAt }) {
    const apply = chat => {
      if (!chat || chat.id !== chatId) return chat;
      const updated = { ...chat };
      if (updated.other_user && updated.other_user.id === userId) {
        updated.other_user = {
          ...updated.other_user,
          last_read_at: lastReadAt,
        };
      }
      if (Array.isArray(updated.members)) {
        updated.members = updated.members.map(m =>
          m.id === userId ? { ...m, last_read_at: lastReadAt } : m
        );
      }
      return updated;
    };
    $state.chats = $state.chats.map(c => (c.id === chatId ? apply(c) : c));
    if ($state.activeChat && $state.activeChat.id === chatId) {
      $state.activeChat = apply($state.activeChat);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
